import { getLocalStorage } from 'utils/handleStorage';
import { AiCallNote } from './types';

/** Sustituye {clave} por su valor en un literal de i18n (i18n() devuelve string; i18Intl no). */
export const fillTemplate = (template: string, vars: Record<string, string | number>): string =>
  Object.keys(vars).reduce((text, key) => text.split(`{${key}}`).join(String(vars[key])), template);

/** Segundos → minutos enteros hacia arriba (la unidad de facturación, D1). */
export const secondsToMinutes = (seconds: number | null | undefined): number => Math.ceil((seconds ?? 0) / 60);

/** mm:ss para duraciones de llamada. */
export const formatDuration = (seconds: number | null | undefined): string => {
  const total = Math.max(0, Math.round(seconds ?? 0));
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
};

export const currentLocale = (): string => {
  const locale = getLocalStorage('locale');
  return typeof locale === 'string' && locale ? locale : 'es';
};

/** 'YYYY-MM' → «octubre 2026» en el idioma de la sesión. */
export const monthLabel = (month: string): string => {
  const [year, mm] = month.split('-').map(Number);
  if (!year || !mm) return month;
  const label = new Date(year, mm - 1, 1).toLocaleDateString(currentLocale(), { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const toMonthKey = (date: Date): string => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

/** Los últimos `count` meses terminando en el actual, del más reciente al más antiguo. */
export const lastMonths = (count = 12, now = new Date()): string[] => {
  const months: string[] = [];
  for (let i = 0; i < count; i += 1) {
    months.push(toMonthKey(new Date(now.getFullYear(), now.getMonth() - i, 1)));
  }
  return months;
};

export const isAiNoteResolved = (note: AiCallNote | null | undefined): boolean =>
  !!note && (note.status === 'ACCEPTED' || note.status === 'EDITED' || note.status === 'DISCARDED');

/** Texto que se enseña como nota: el borrador final si existe; si no, la provisional (en directo). */
export const aiNoteText = (note: AiCallNote | null | undefined): string =>
  (note?.status === 'LIVE' ? note?.liveNote ?? note?.draft : note?.draft ?? note?.liveNote) ?? '';

// --- Errores de negocio de aceptar/descartar (HTTP 200, success=false) ---

/** La nota ya estaba aceptada, editada o descartada (otro operador, otra pestaña…). */
export const AI_NOTE_ALREADY_RESOLVED = 6100;
/** No hay nota para ese evento. */
export const AI_NOTE_NOT_FOUND = 6101;

/** Clave i18n del mensaje para un errorCode de aceptar/descartar (genérico si no es uno de los suyos). */
export const aiNoteErrorKey = (errorCode: number | null | undefined): string =>
  errorCode === AI_NOTE_ALREADY_RESOLVED || errorCode === AI_NOTE_NOT_FOUND
    ? `aiNotes.error.${errorCode}`
    : 'error.api.generic';

/**
 * true si `incoming` es una foto vieja de la misma nota que `current` y no debe pisarla. El back
 * nunca reabre una nota resuelta y updatedTmst solo crece, así que:
 *  - una resuelta no vuelve a DRAFT/LIVE con una respuesta de updatedTmst menor o igual (sondeo
 *    lanzado antes de aceptar que llega después);
 *  - en general, una respuesta con updatedTmst menor que la que ya se tiene se descarta.
 */
export const isStaleAiNote = (current: AiCallNote | null | undefined, incoming: AiCallNote | null | undefined): boolean => {
  if (!current || !incoming || current.eventId !== incoming.eventId) return false;
  const currentTmst = current.updatedTmst ?? 0;
  const incomingTmst = incoming.updatedTmst ?? 0;
  if (isAiNoteResolved(current) && !isAiNoteResolved(incoming)) return incomingTmst <= currentTmst;
  return incomingTmst < currentTmst;
};

// --- Ritmo del sondeo de la nota de la llamada seguida (AiNoteLiveTracker) ---

/** En llamada con nota, y durante el primer minuto tras colgar (en tiempo real el borrador llega ~4 s después). */
export const LIVE_POLL_MS = 3000;
/** En llamada sin nota (grupo sin la función), tras el primer minuto colgada o con el servicio fallando. */
export const LIVE_SLOW_POLL_MS = 15000;
export const AFTER_HANGUP_FAST_MS = 60 * 1000;
/** Tope total tras colgar: a partir de aquí la nota «llegará al expediente cuando esté lista». */
export const AFTER_HANGUP_MAX_MS = 15 * 60 * 1000;
/** Una nota aún en directo que no cambia en este tiempo con la llamada colgada se da por parada. */
export const STALE_LIVE_MS = 2 * 60 * 1000;

/** Huella de lo que cambia de una nota entre sondeos (para saber desde cuándo está quieta). */
export const aiNoteSignature = (note: AiCallNote | null | undefined): string =>
  note ? `${note.status}|${note.updatedTmst ?? ''}|${note.transcript?.length ?? 0}` : '';

export type LivePollStep = number | 'stop' | 'expire';

/**
 * Qué hacer tras cada respuesta del sondeo: esperar n ms, parar (ya no hay nada que esperar) o
 * expirar (dejar de esperar: el bloque pasa a «llegará al expediente» y la lista deja de omitirla).
 *  - borrador o resuelta: parar (un DRAFT no cambia solo; aceptar/descartar repide por su cuenta);
 *  - en llamada: cada 3 s con nota, cada 15 s sin ella (grupos sin IA) o si falla;
 *  - colgada: sin nota → parar; 3 s el primer minuto y luego 15 s, hasta el tope de 15 min o hasta
 *    que una nota en directo lleve 2 min sin cambiar → expirar.
 * lastChangeAt es el reloj LOCAL del último cambio observado (no se compara updatedTmst del servidor
 * con el reloj del puesto, que puede ir desfasado).
 */
export const nextLivePoll = ({
  item,
  hangupAt,
  lastChangeAt,
  now,
  failed = false,
}: {
  item: AiCallNote | null;
  hangupAt: number | null;
  lastChangeAt: number;
  now: number;
  failed?: boolean;
}): LivePollStep => {
  if (item && (item.status === 'DRAFT' || isAiNoteResolved(item))) return 'stop';
  if (hangupAt === null) return item && !failed ? LIVE_POLL_MS : LIVE_SLOW_POLL_MS;
  const sinceHangup = now - hangupAt;
  if (sinceHangup >= AFTER_HANGUP_MAX_MS) return 'expire';
  if (!item) return failed ? LIVE_SLOW_POLL_MS : 'stop';
  if (item.status === 'LIVE' && now - Math.max(lastChangeAt, hangupAt) >= STALE_LIVE_MS) return 'expire';
  return sinceHangup < AFTER_HANGUP_FAST_MS && !failed ? LIVE_POLL_MS : LIVE_SLOW_POLL_MS;
};

/** Tope del sondeo de la lista del expediente desde el último cambio de sus notas en directo. */
export const LIST_LIVE_POLL_MAX_MS = 15 * 60 * 1000;
